import React, { memo } from 'react';
import { useDispatch } from 'react-redux';

import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { alertRulesActions, alertRulesTypes } from 'app-components/flux/alert-rules';
import QTip from 'components/QuickenControls/QTip';
import { makeStyles } from 'tss-react/mui';

import EmailIcon from '@mui/icons-material/Email';
import NotificationAddIcon from '@mui/icons-material/NotificationAdd';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';

import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
// NOTE: The Push svg is not centered properly, so the padding is slightly adjusted so the outline actually appears correct
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import type { AlertRuleProps } from 'app-components/flux/alert-rules/alertRulesTypes';
import type { RecordOf } from 'immutable';
import Dump from 'components/Dump';
import NotificationSubtitle from './notificationSubtitle';

const useStyles = makeStyles()((theme) => ({
  gridItem: {
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  notificationRowSubtitles: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    '@media (max-width: 1094px)': {
      textWrap: 'nowrap',
    },
  },
  chipsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    '@media (max-width: 1094px)': {
      gap: theme.spacing(3),
    },
  },
  chipRoot: {
    borderRadius: 6,
    width: 75,
    height: 24,
  },
  disabledRoot: {
    opacity: 0.45,
  },
  dividerMargin: {
    marginRight: theme.spacing(2),
    marginLeft: theme.spacing(2),
  },
}));

const getChips = (alertRule: RecordOf<AlertRuleProps>, isChecked: boolean) => {
  const { isEmailSupported, isPushSupported, isInProductSupported, isEmailOk, isPushOk, isInProductOk } =
    alertRule.preferences;

  return [
    {
      key: 'email',
      label: 'Email',
      title: !isEmailSupported ? 'Not supported' : isEmailOk && isChecked ? 'Enabled' : 'Disabled',
      updatePayload: {
        id: alertRule.id,
        preferences: { isEmailOk: !isEmailOk },
      },
      disabled: !isEmailSupported,
      icon: <EmailIcon fontSize='inherit' />,
      ariaLabel: 'Toggle Email',
      isEnabled: isEmailOk,
    },
    {
      key: 'push',
      label: 'Push',
      title: !isPushSupported ? 'Not supported' : isPushOk && isChecked ? 'Enabled' : 'Disabled',
      updatePayload: {
        id: alertRule.id,
        preferences: { isPushOk: !isPushOk },
      },
      disabled: !isPushSupported,
      icon: <PhoneIphoneIcon fontSize='inherit' />,
      ariaLabel: 'Toggle Push',
      isEnabled: isPushOk,
    },
    {
      key: 'inApp',
      label: 'In app',
      title: !isInProductSupported ? 'Not supported' : isInProductOk && isChecked ? 'Enabled' : 'Disabled',
      updatePayload: {
        id: alertRule.id,
        preferences: { isInProductOk: !isInProductOk },
      },
      disabled: !isInProductSupported,
      icon: <NotificationAddIcon fontSize='inherit' />,
      ariaLabel: 'Toggle In app',
      isEnabled: isInProductOk,
    },
  ];
};

function NotificationRowSetting({
  isAllPaused,
  alertRule,
}: {
  isAllPaused: boolean;
  alertRule: RecordOf<AlertRuleProps>;
}) {
  const { classes, cx } = useStyles();
  const dispatch = useDispatch();
  const checked = alertRule.preferences.isEnabled || false;
  const chips = getChips(alertRule, checked);

  return (
    <Paper sx={{ p: 2, width: '100%', marginY: 0.5 }}>
      <Dump value={alertRule} />
      <Grid container className={classes.gridItem}>
        <Grid item sm={5} className={cx(classes.notificationRowSubtitles, isAllPaused && classes.disabledRoot)}>
          <Typography variant='bodyMedium'>{alertRule.title}</Typography>
          <NotificationSubtitle alertRule={alertRule} />
        </Grid>
        <div className={classes.chipsRow}>
          {chips.map((chipItem) => (
            <Grid item key={chipItem.key}>
              {chipItem.disabled ? (
                <div className={classes.chipRoot}></div>
              ) : (
                <QTip title={chipItem.title}>
                  <Chip
                    data-testid={`toggle-${chipItem.title}-${chipItem.key}-button`}
                    icon={chipItem.icon}
                    label={chipItem.label}
                    aria-label={chipItem.ariaLabel}
                    size='medium'
                    variant='tonal'
                    color={chipItem.isEnabled && checked ? 'primary' : 'inactive'}
                    onClick={() =>
                      dispatch(
                        alertRulesActions.updateAlertRule(alertRulesTypes.mkAlertRuleUpdate(chipItem.updatePayload)),
                      )
                    }
                    disabled={chipItem.disabled || isAllPaused || !checked}
                  />
                </QTip>
              )}
            </Grid>
          ))}
          <Divider flexItem orientation='vertical' className={classes.dividerMargin} />
          <Switch
            size='small'
            color='primary'
            checked={checked}
            disabled={isAllPaused}
            onChange={(value) =>
              dispatch(
                alertRulesActions.updateAlertRule(
                  alertRulesTypes.mkAlertRuleUpdate({
                    id: alertRule.id,
                    preferences: { isEnabled: value.target.checked },
                  }),
                ),
              )
            }
          />
        </div>
      </Grid>
    </Paper>
  );
}

export default memo(NotificationRowSetting);
